import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  forwardRef,
  InjectionToken,
  input,
  model,
  Signal,
} from '@angular/core';

/** Lo que el grupo necesita de cada opción. Lo implementan `ui-radio-row` y las fichas de día. */
export interface RadioItem {
  readonly value: Signal<string>;
  readonly disabled: Signal<boolean>;
  focus(): void;
}

export interface RadioGroupController {
  readonly value: Signal<string | null>;
  readonly tabbable: Signal<string | null>;
  select(item: RadioItem): void;
}

export const UI_RADIO_GROUP = new InjectionToken<RadioGroupController>('UI_RADIO_GROUP');
export const UI_RADIO_ITEM = new InjectionToken<RadioItem>('UI_RADIO_ITEM');

/**
 * Selección única con el patrón de radio de ARIA: una sola parada de tabulador (foco itinerante), y
 * las flechas mueven y eligen. Una opción deshabilitada se anuncia, pero las flechas la saltan.
 */
@Component({
  selector: 'ui-radio-group',
  template: `<ng-content />`,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      gap: var(--ui-space-2);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: UI_RADIO_GROUP, useExisting: forwardRef(() => RadioGroupComponent) }],
  host: {
    role: 'radiogroup',
    '[attr.aria-label]': 'label()',
    '(keydown)': 'onKeydown($event)',
  },
})
export class RadioGroupComponent implements RadioGroupController {
  readonly label = input.required<string>();
  readonly value = model<string | null>(null);

  private readonly items = contentChildren(UI_RADIO_ITEM, { descendants: true });

  /** A dónde cae el tabulador: la opción elegida o, si no hay o está deshabilitada, la primera disponible. */
  readonly tabbable = computed(() => {
    const enabled = this.items().filter((item) => !item.disabled());
    const selected = enabled.find((item) => item.value() === this.value());
    return (selected ?? enabled[0])?.value() ?? null;
  });

  select(item: RadioItem): void {
    if (!item.disabled()) this.value.set(item.value());
  }

  onKeydown(event: KeyboardEvent): void {
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
    if (!step) return;

    const enabled = this.items().filter((item) => !item.disabled());
    if (!enabled.length) return;

    event.preventDefault();
    const current = enabled.findIndex((item) => item.value() === this.tabbable());
    const next = enabled[(current + step + enabled.length) % enabled.length];
    this.select(next);
    next.focus();
  }
}
